import {
    Checkbox,
    Divider,
    Icon,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import { SyntheticEvent, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ProjectSearchType } from "../../../api/psc";
import { HelpIconButton } from "../../../components/exports";
import { DialogContext } from "../../../contexts/DialogContext";
import { SnackbarContext } from "../../../contexts/SnackbarContext";
import {
    clearSelectedProjects,
    setSelectedProjects,
} from "../../../slices/SharedSlice";
import { RootState } from "../../../slices/Store";
import BatchUpdateProjectVisibilityDialog from "../dialogs/BatchUpdateProjectVisibilityDialog";
import SelectFolderForBatchDialog from "../dialogs/SelectFolderForBatchDialog";
import PrintSelection from "../dialogs/SelectionPrintDialog";
import SmsCampaignFromProjectSelectionDialog from "../dialogs/SmsCampaignFromProjectSelectionDialog";
import { ProjectState } from "../slice/ProjectSlice";
import { PromotedProjectState } from "../slice/PromotedProjectSlice";

export default function DesktopProjectToolbox(props: {
    fetchProjects: () => void;
    setProjectType: (ProjectSearchType) => void;
    state: ProjectState | PromotedProjectState;
    isRequirements: boolean;
}) {
    const dispatch = useDispatch();

    const sharedState = useSelector((state: RootState) => state.shared);

    const { showSnackbar } = useContext(SnackbarContext);
    const { openDialog } = useContext(DialogContext);

    const handleTypeChange = (
        event: SyntheticEvent,
        newValue: ProjectSearchType
    ) => {
        props.setProjectType(newValue as ProjectSearchType);
    };

    const openCustomPrintDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }

        openDialog(
            <PrintSelection projects={sharedState.batchOperationProjects} />,
            (printPerformed) => {
                if (printPerformed) {
                    props.fetchProjects();
                }
            }
        );
    };

    const openSelectionSmsCampaignDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }
        openDialog(
            <SmsCampaignFromProjectSelectionDialog
                projects={sharedState.batchOperationProjects}
                onSent={() => {
                    dispatch(clearSelectedProjects());
                }}
            />
        );
    };

    const openSelectionFoldersDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }
        openDialog(
            <SelectFolderForBatchDialog
                projects={sharedState.batchOperationProjects}
                onMoved={() => {
                    props.fetchProjects();
                }}
            />
        );
    };

    const openDeleteBatchProjectDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }
        openDialog(
            <BatchUpdateProjectVisibilityDialog
                projects={sharedState.batchOperationProjects}
                onApplied={() => {
                    props.fetchProjects();
                }}
            />
        );
    };

    const toggleAll = () => {
        if (sharedState.batchOperationProjects.length > 0) {
            dispatch(clearSelectedProjects());
        } else {
            dispatch(setSelectedProjects(props.state.projects));
        }
    };

    const isTheAllTabSelected =
        props.state.filter.type === ProjectSearchType.ALL;

    return (
        <Stack
            direction="row"
            spacing={1}
            sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }}
            alignItems="center"
        >
            <Tabs
                value={props.state.filter.type}
                onChange={handleTypeChange}
                variant="standard"
                sx={{ flexGrow: 1 }}
            >
                {sharedState.accessList.length > 0 && (
                    <Tab value={ProjectSearchType.NEW} label="جدید" />
                )}
                {sharedState.accessList.length > 0 && (
                    <Tab value={ProjectSearchType.VISITED} label="مشاهده شده" />
                )}
                {!props.isRequirements && (
                    <Tab value={ProjectSearchType.ALL} label="همه" />
                )}
            </Tabs>

            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ py: 1, boxSizing: "border-box", height: 35 }}
            >
                <Stack direction="row" alignItems="center">
                    <Checkbox
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                        checked={
                            sharedState.batchOperationProjects.length ===
                            props.state.projects.length
                        }
                        onChange={toggleAll}
                    />
                    <Typography color="text.secondary">انتخاب همه</Typography>
                </Stack>

                <Divider variant="middle" flexItem orientation="vertical" />

                <Stack direction="row" alignItems="center" spacing={1}>
                    <HelpIconButton tag="projects" />

                    <Tooltip
                        title={
                            isTheAllTabSelected
                                ? "برای استفاده از امکان پرینت پروژه‌ها باید از طریق تب جدید یا مشاهده شده اقدام کنید."
                                : "پرینت پروژه ها"
                        }
                    >
                        <span>
                            <IconButton
                                color="primary"
                                disabled={isTheAllTabSelected}
                                onClick={openCustomPrintDialog}
                            >
                                <Icon>print</Icon>
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="پیامک به سازندگان پروژه ها">
                        <IconButton
                            color="secondary"
                            disabled={isTheAllTabSelected}
                            onClick={openSelectionSmsCampaignDialog}
                        >
                            <Icon>sms</Icon>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="انتقال پروژه ها به پوشه">
                        <IconButton
                            color="warning"
                            onClick={openSelectionFoldersDialog}
                        >
                            <Icon>folder</Icon>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="حذف گروهی پروژه ها">
                        <IconButton
                            color="error"
                            onClick={openDeleteBatchProjectDialog}
                        >
                            <Icon>delete</Icon>
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Divider variant="middle" flexItem orientation="vertical" />

                <Stack direction="row" spacing={0.3} alignItems="center">
                    <Typography color="text.secondary" fontSize="smaller">
                        {props.state.totalCount.toLocaleString()}
                    </Typography>

                    <Typography color="text.secondary" fontSize="small">
                        پروژه
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}
